<template>
  <v-container class="d-flex justify-center align-center login-container" fluid>
    <v-row no-gutters class="fill-height">
      <v-col class="justify-center d-flex">
        <v-card class="pa-5" elevation="5" min-width="400">
          <v-card-title class="justify-center">
            <v-img
              :src="'/assets/images/logos/' + client_logo_name"
              max-width="200"
              class="mb-4"
            />
          </v-card-title>

          <v-form v-model="valid" @submit.prevent="login">
            <v-snackbar
              v-model="showError"
              :timeout="4000"
              bottom
              right
              class="custom-error-snackbar"
            >
              <div class="error-content">
                <v-icon class="error-icon">mdi-alert-circle</v-icon>
                <span>{{ errorMessage }}</span>
              </div>
            </v-snackbar>
            <div class="d-flex flex-column gap-3">
              <v-text-field
                v-if="firstStep"
                v-model="username"
                :label="$t('res_Login_Username')"
                solo
                append-icon="mdi-account"
                :rules="[rules.required]"
              />
              <div class="otp-container otp-ltr">
                <v-otp-input
                  v-if="isOtpFlow && firstStepDone"
                  v-model="otp"
                  :rules="[rules.required]"
                  length="6"
                ></v-otp-input>
              </div>
              <v-btn
                v-if="firstStepDone"
                :disabled="otp?.length === 0"
                type="submit"
                class="primary-new-button"
                block
              >
                {{ $t('res_Login_VerifyOTP') }}
              </v-btn>
              <div v-if="isOtpFlow && !firstStepDone" class="captcha-container">
                <div class="captcha-box" :class="{ 'captcha-shake': captchaError }">
                  <div class="captcha-question">
                    <span class="captcha-number">{{ captchaNum1 }}</span>
                    <span class="captcha-operator">+</span>
                    <span class="captcha-number">{{ captchaNum2 }}</span>
                    <span class="captcha-equals">=</span>
                    <span class="captcha-placeholder">?</span>
                  </div>
                  <v-btn
                    icon
                    x-small
                    :class="['captcha-refresh', { 'btn-spin': isRefreshing }]"
                    @click="refreshCaptcha"
                    :title="$t('res_Login_CaptchaRefresh')"
                  >
                    <v-icon small>mdi-cached</v-icon>
                  </v-btn>
                </div>
                <v-text-field
                  v-model="captchaAnswer"
                  :label="$t('res_Login_CaptchaAnswer')"
                  :placeholder="$t('res_Login_CaptchaPlaceholder')"
                  solo
                  dense
                  type="number"
                  :error="captchaError"
                  :error-messages="captchaError ? $t('res_Login_CaptchaError') : ''"
                  @input="captchaError = false"
                  class="captcha-input"
                />
              </div>

              <div v-if="isOtpFlow" class="d-flex justify-center">
                <v-btn
                  :disabled="username === '' || otpSent || captchaAnswer === ''"
                  v-if="!firstStepDone"
                  @click="validateAndSendOtp"
                  class="primary-new-button"
                  block
                >
                  {{ $t('res_Login_SendOTP') }}
                </v-btn>
                <span v-if="otpSent && firstStepDone">{{ timer }} {{ $t('res_Login_SecondsRemaining') }}</span>
                <v-btn
                  v-if="otpSent && timer === 0"
                  @click="resendOtp"
                  class="primary-new-button"
                  block
                >
                  {{ $t('res_Login_ResendOTP') }}
                </v-btn>
              </div>
            </div>
            <hr />
          </v-form>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import axios from "axios";
import constants from "@/assets/js/constants.js";
export default {
  name: "Login",
  data() {
    return {
      client_logo_name: client_logo_name,
      username: "",
      password: "",
      otp: "",
      valid: false,
      errorMessage: "",
      showError: false,
      isOtpFlow: true, // Controls the OTP input field visibility
      otpSent: false, // Tracks if the OTP has been sent
      timer: 300, // 5 minutes timer for OTP
      timerInterval: null, // Timer interval reference for clearing,
      firstStepDone: false, // Tracks if the first step of OTP flow is done
      firstStep: true,
      captchaNum1: 0,
      captchaNum2: 0,
      captchaAnswer: "",
      captchaError: false,
      isRefreshing: false,
      rules: {
        required: (value) => !!value || this.$t('res_Login_FieldRequired'),
      },
    };
  },
  beforeMount() {
    localStorage.removeItem("user");
  },
  mounted() {
    this.generateCaptcha();
  },
  methods: {
    login() {
      if (this.isOtpFlow) {
        // Handle OTP verification and login
        this.verifyOtp();
      } else if (this.otp && this.firstStep == false) {
        // Regular login logic (email/password login)
        this.sendOtp(this.otp);
      } else {
        //regular login logic
        if (this.username && this.password) {
          //todo moath change this to base url
          axios
            .post(constants.baseApiUrl + "auth/login", {
              email: this.username,
              password: this.password,
              rememberMe: true,
              loginType: 1,
            })
            .then((response) => {
              console.log(response);
              if (response.data.success && response.data.success === true) {
                localStorage.setItem("user", response.data.data.access_token);
                this.$router.push({ name: "inbox" });
              } else {
                this.errorMessage = response.data.message;
                this.showError = true;
              }
            })
            .catch((error) => {
              console.log(error);
            });
        } else {
          this.errorMessage = this.$t('res_Login_EnterCredentials');
          this.showError = true;
        }
      }
    },
    // Switch to OTP flow
    toggleEmailLogin() {
      this.isOtpFlow = true;
      this.valid = false;
      this.errorMessage = "";
    },
    // Send OTP and start the timer
    sendOtp(otp) {
      // Simulate OTP sending
      this.otpSent = true;
      this.startTimer();
      this.errorMessage = ""; // Clear any previous errors
      console.log("OTP sent to", this.username); // Replace with actual API call for OTP
      axios
        .post(constants.baseApiUrl + `auth/login?email=${this.username}`)
        .then((response) => {
          if (response.data.success === true) {
            this.firstStep = false;
            this.firstStepDone = true;
          } else {
            this.errorMessage = response.data.message;
            this.showError = true;
            this.otpSent = false;
          }
        })
        .catch((error) => {
          console.log(error);
        });
    },
    // Verify the OTP
    verifyOtp() {
      axios
        .post(constants.baseApiUrl + "Auth/Verify-otp", {
          email: this.username,
          otp: this.otp,
        })
        .then((response) => {
          if (response.data.success === true) {
            localStorage.setItem("user", response.data.data.access_token);

            this.$router.push({ name: "inbox" });
          } else {
            this.errorMessage = response.data.message;
            this.showError = true;
          }
        })
        .catch((error) => {
          console.log(error);
        });
    },
    // Start the timer
    startTimer() {
      this.timerInterval = setInterval(() => {
        if (this.timer > 0) {
          this.timer -= 1;
        } else {
          clearInterval(this.timerInterval);
        }
      }, 1000);
    },
    // Resend OTP
    resendOtp() {
      this.timer = 300;
      this.sendOtp();
    },
    // Redirect to UAE-PASS URL
    redirectToUaePass() {
      window.location.href =
        "http://localhost:8586/login_uaepass.html?isStartLogin=1"; // Replace with actual UAE-PASS URL
    },
    generateCaptcha() {
      this.captchaNum1 = Math.floor(Math.random() * 10) + 1; // 1-10
      this.captchaNum2 = Math.floor(Math.random() * 10) + 1; // 1-10
      this.captchaAnswer = "";
      this.captchaError = false;
    },
    refreshCaptcha() {
      this.isRefreshing = true;
      setTimeout(() => {
        this.generateCaptcha();
        this.isRefreshing = false;
      }, 500);
    },
    validateCaptcha() {
      const correctAnswer = this.captchaNum1 + this.captchaNum2;
      return parseInt(this.captchaAnswer) === correctAnswer;
    },
    validateAndSendOtp() {
      if (this.validateCaptcha()) {
        this.captchaError = false;
        this.sendOtp();
      } else {
        this.captchaError = true;
        this.errorMessage = this.$t('res_Login_CaptchaError');
        this.showError = true;
        setTimeout(() => {
          this.captchaError = false;
          this.generateCaptcha(); 
        }, 600);
      }
    },
  },
  beforeDestroy() {
    // Clear timer interval on component destroy
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
    }
  },
};
</script>

<style scoped>
.v-btn {
  transition: background-color 0.3s;
}

.v-btn:hover {
  background-color: #5c6bc0;
}

.v-row {
  margin-top: 20px;
}

.v-col {
  display: flex;
  align-items: center;
  justify-content: center;
}

.v-btn {
  margin-top: 10px;
}

hr {
  border-top: 2px solid #ddd;
}

.otp-ltr {
  direction: ltr !important;
}

.otp-ltr .v-otp-input,
.otp-ltr input {
  direction: ltr !important;
  text-align: center !important;
}

.captcha-container {
  margin: 10px 0;
}

.captcha-box {
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--primary-control-color, #ECF5FF);
  border: 1px solid var(--primary-button-color, #816238);
  border-radius: 8px;
  padding: 15px 20px;
  margin-bottom: 10px;
}

.captcha-question {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 24px;
  font-weight: bold;
  color: var(--primary-button-color, #816238);
  user-select: none;
}

.captcha-number {
  background: var(--primary-button-color, #816238);
  color: #ffffff;
  padding: 5px 12px;
  border-radius: 5px;
  min-width: 40px;
  text-align: center;
}

.captcha-operator,
.captcha-equals {
  color: var(--primary-button-color, #816238);
}

.captcha-placeholder {
  background: var(--secondary--color-btns, #EFF2F5);
  color: var(--primary-button-color, #816238);
  padding: 5px 15px;
  border-radius: 5px;
  font-size: 20px;
  border: 1px dashed var(--primary-button-color, #816238);
}

.captcha-refresh {
  margin-left: 15px;
  color: var(--primary-button-color, #816238) !important;
  background: var(--secondary--color-btns, #EFF2F5) !important;
  border: 1px solid var(--primary-button-color, #816238) !important;
  transition: background 0.3s ease, color 0.3s ease;
}

.captcha-refresh:hover {
  background: var(--primary-control-hover-color, #b89438) !important;
  color: #ffffff !important;
}

.captcha-refresh.btn-spin {
  animation: spin 0.5s linear;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.captcha-input {
  margin-top: 5px;
}

.captcha-input input {
  text-align: center !important;
  font-size: 18px !important;
  font-weight: bold !important;
  color: var(--primary-button-color, #816238) !important;
}

.captcha-shake {
  animation: shake 0.5s ease-in-out;
  border: 2px solid #ff5252 !important;
  background: #ffebee !important;
}

@keyframes shake {
  0%, 100% { transform: translateX(0); }
  10%, 30%, 50%, 70%, 90% { transform: translateX(-5px); }
  20%, 40%, 60%, 80% { transform: translateX(5px); }
}

::v-deep .custom-error-snackbar .v-snack__wrapper {
  background: var(--primary-control-color, #ECF5FF) !important;
  border: 1px solid var(--primary-button-color, #816238) !important;
  border-left: 4px solid #c62828 !important;
  border-radius: 8px !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
}

::v-deep .custom-error-snackbar .v-snack__content {
  padding: 12px 16px !important;
}

.error-content {
  display: flex;
  align-items: center;
  gap: 10px;
  color: var(--primary-button-color, #816238);
  font-weight: 500;
  font-size: 14px;
}

.error-icon {
  color: #c62828 !important;
  font-size: 22px !important;
}
</style>
